import { useRef, useEffect } from 'react'

interface Props {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  disabled?: boolean
  error?: string
}

export function IntegrationCodeInput({ value, onChange, onSubmit, disabled, error }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')

    if (input.length > 15) {
      input = input.slice(0, 15)
    }

    onChange(input)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.length === 15 && !disabled) {
      onSubmit()
    }
  }

  const formatDisplay = (code: string) => {
    return code.match(/.{1,3}/g)?.join(' ') || code
  }

  const progress = (value.length / 15) * 100
  const isComplete = value.length === 15

  return (
    <div className="space-y-4">
      {/* Input Container */}
      <div className="relative group">
        {/* Glow Effect */}
        <div
          className={`absolute -inset-0.5 rounded-xl bg-gradient-to-r from-white/20 via-white/10 to-white/20 opacity-0 blur transition-opacity duration-500 ${
            isComplete ? 'opacity-100' : 'group-focus-within:opacity-60'
          }`}
        />

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={formatDisplay(value)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="ABC 123 XYZ 456 789"
          className={`relative w-full px-6 py-5 bg-black/80 backdrop-blur-sm border-2 ${
            error
              ? 'border-red-500/50 focus:border-red-500'
              : isComplete
                ? 'border-white/40'
                : 'border-white/10 focus:border-white/30'
          } rounded-xl text-center text-2xl font-mono font-bold text-white tracking-[0.2em] placeholder:tracking-[0.15em] placeholder:text-white/20 focus:outline-none transition-all duration-300 disabled:opacity-50`}
          maxLength={19}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ease-out rounded-full ${
              error ? 'bg-red-500' : isComplete ? 'bg-white' : 'bg-white/40'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Status Text */}
        <div className="flex items-center justify-between text-xs">
          <span className={error ? 'text-red-400' : 'text-gray-500'}>
            {error || (isComplete ? 'Pronto para conectar' : 'Digite o código de integração')}
          </span>
          <span className={`font-mono ${isComplete ? 'text-white' : 'text-gray-600'}`}>
            {value.length}/15
          </span>
        </div>
      </div>
    </div>
  )
}
